import { useMutation, useQueryClient } from "@tanstack/react-query";
import { OrderHeader, OrderItem, OrderItems, OrderPayment } from "../screens/orders/ordersGridTypes.types";
import { api } from "./config";
import { createOrderItem, getOrderPaymentsToSave } from "./useCreateOrder";
import { ROLES } from "../utils/constants";
import { useUser } from "./useAuth";
import { areDatesEqual } from "../utils/dateUtils";

type OrderPayments = {
    order_id?: number;
    orderPayments?: Array<OrderPayment>;
    order_payments_to_delete?: Array<number>;
};

export const useUpdateOrder = (currentOrder?: any) => {
    const queryClient = useQueryClient();
    const { data: user } = useUser();
    return useMutation({
        mutationFn: (formData: any) => updateOrder(formData, currentOrder, user),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ predicate: (query: any) => query.queryKey[0].includes(`orders-${data.id}`) });
            queryClient.invalidateQueries({
                predicate: (query: any) => query.queryKey[0].includes(`order_items-items-parentId-${data.id}`),
            });
            queryClient.invalidateQueries({
                predicate: (query: any) => query.queryKey[0].includes(`orders_payments-items-parentId-${data.id}`),
            });
        },
    });
};

const updateOrder = async (formData: any, currentOrder?: any, user?: any) => {
    const orderHeaderData = {
        clinic: formData.clinic?.id,
        order_id: formData.order_id,
        treatment: formData.treatment?.id,
        payment_status: formData.payment_status,
        order_date: formData.order_date,
        notes: formData.notes || null,
        payment_date: formData.payment_status === "paid" ? formData.payment_date : null,
    } as OrderHeader;

    const orderItemsData = {
        order_id: formData.order_id,
        orderItems: formData.order_items as OrderItem[],
        order_items_to_delete: formData.order_items_to_delete,
    } as OrderItems;

    const orderPaymentsData = {
        order_id: formData.order_id,
        orderPayments: getOrderPaymentsToSave(formData),
        order_payments_to_delete: formData.order_payments_to_delete,
    } as OrderPayments;
    
    const isOrderItemsChanged = haveOrderItemsChanged(currentOrder?.order_items, formData.order_items);

    if (currentOrder && currentOrder.invoice_number && isOrderItemsChanged && user?.role.id !== ROLES.admin) {
        const error = {
            response: {
                data: {
                    errors: [
                        {
                            message:
                                "Não pode alterar os serviços da Compra porque já foi emitida fatura e enviado link de pagamento para o cliente",
                            extensions: {
                                code: "INVALID_PAYLOAD",
                            },
                        },
                    ],
                },
            },
        };
        throw error;
    }

    const orderHeaderdirtyFields = getOrderHeaderDirtyFields(currentOrder, orderHeaderData);
    const isOrderHeaderChanged = orderHeaderdirtyFields.length > 0;

    if (isOrderHeaderChanged) {
        const response = await updateOrderHeader(orderHeaderData, orderHeaderdirtyFields);
        await updateOrderItems(orderItemsData);
        await updateOrderPayments(orderPaymentsData);
        return response.data.data;
    }

    await updateOrderItems(orderItemsData);
    await updateOrderPayments(orderPaymentsData);
    return { id: orderHeaderData.order_id };
};

const updateOrderItems = async (order: OrderItems) => {
    if (!order.orderItems) return;

    await Promise.all(
        order.orderItems.map(async (item) => {
            if (item.newRow && order.order_id) {
                await createOrderItem(order.order_id, item);
            } else {
                await api({
                    method: "PATCH",
                    url: `items/order_items/${item.order_item_id}`,
                    data: {
                        service_variant: item.variantId,
                        quantity: item.quantity,
                        price: item.price,
                        discount: item.discount,
                    },
                });
            }
        })
    );

    if (order.order_items_to_delete && order.order_items_to_delete.length > 0) {
        await deleteOrderItems(order.order_items_to_delete);
    }
};

const updateOrderHeader = async (order: OrderHeader, dirtyFields: Array<string>) => {
    const orderId = order.order_id;

    const updatedOrder = Object.fromEntries(
        dirtyFields.map((field) => [field, order[field as keyof OrderHeader]])
    );
    return await api({
        method: "PATCH",
        url: `items/orders/${orderId}`,
        data: updatedOrder,
    });
};

const deleteOrderItems = async (orderItemsToDelete: OrderItem[]) => {
    if (!orderItemsToDelete || orderItemsToDelete.length === 0) return;

    const orderItemsIds = mapOrderItemsIds(orderItemsToDelete);

    if (orderItemsIds.length === 0) return;

    await api({
        method: "DELETE",
        url: `items/order_items`,
        data: orderItemsIds,
    });
    return orderItemsIds;
};

const mapOrderItemsIds = (orderItemsToDelete: OrderItem[]) => {
    return orderItemsToDelete.map((item) => item.order_item_id).filter((orderItemId) => orderItemId !== undefined);
};

const updateOrderPayments = async (orderPayments: OrderPayments) => {
    if (!orderPayments.orderPayments) return;

    await Promise.all(
        orderPayments.orderPayments.map(async (payment) => {
            if (payment.order !== orderPayments.order_id) return;

            if (!payment.order_payment_id && orderPayments.order_id) {
                await api({
                    method: "POST",
                    url: `items/orders_payments`,
                    data: {
                        order: orderPayments.order_id,
                        payment_method: payment.payment_method,
                        amount: payment.amount,
                        payment_date: payment.payment_date,
                    },
                });
            } else {
                await api({
                    method: "PATCH",
                    url: `items/orders_payments/${payment.order_payment_id}`,
                    data: {
                        payment_method: payment.payment_method,
                        amount: payment.amount,
                        payment_date: payment.payment_date,
                    },
                });
            }
        })
    );

    if (orderPayments.order_payments_to_delete && orderPayments.order_payments_to_delete.length > 0) {
        await deleteOrderPayments(orderPayments.order_payments_to_delete);
    }
};

const deleteOrderPayments = async (orderPaymentsToDelete: Array<number>) => {
    if (!orderPaymentsToDelete || orderPaymentsToDelete.length === 0) return;

    await api({
        method: "DELETE",
        url: `items/orders_payments`,
        data: orderPaymentsToDelete,
    });

    return orderPaymentsToDelete;
};

const getOrderHeaderDirtyFields = (currentOrder: any, orderHeaderData: any) => {
    if (!currentOrder) return Object.keys(orderHeaderData).filter((field) => field !== "order_id");

    const currentClinic = currentOrder.clinic?.id ?? currentOrder.clinic;
    const currentTreatment = currentOrder.treatment?.id ?? currentOrder.treatment;

    const dirtyFields: Array<string> = [];

    if (currentClinic !== orderHeaderData.clinic) dirtyFields.push("clinic");
    if (currentTreatment !== orderHeaderData.treatment) dirtyFields.push("treatment");
    if (currentOrder.payment_status !== orderHeaderData.payment_status) dirtyFields.push("payment_status");
    if (!areDatesEqual(currentOrder.order_date, orderHeaderData.order_date)) dirtyFields.push("order_date");
    if ((currentOrder.notes || null) !== (orderHeaderData.notes || null)) dirtyFields.push("notes");
    if (!areDatesEqual(currentOrder.payment_date, orderHeaderData.payment_date)) dirtyFields.push("payment_date");

    return dirtyFields;
};

const haveOrderItemsChanged = (currentOrderItems: Array<any>, formOrderItems: Array<any>) => {
    if (!currentOrderItems || currentOrderItems.length === 0) return false;
    if (currentOrderItems.length !== formOrderItems.length) return true;

    const formOrderItemsMap = new Map(
        formOrderItems.map((item) => [
            item.order_item_id,
            {
                sku: item.sku,
                quantity: item.quantity,
                price: item.price,
            },
        ])
    );

    for (const currentItem of currentOrderItems) {
        const formItem = formOrderItemsMap.get(currentItem.id);

        if (!formItem) return true;

        const currentSku = currentItem.service_variant?.sku;
        const hasSkuChanged = currentSku !== formItem.sku;
        const hasQuantityChanged = currentItem.quantity !== formItem.quantity;
        const hasPriceChanged = currentItem.price !== formItem.price;

        if (hasSkuChanged || hasQuantityChanged || hasPriceChanged) return true;
    }

    return false;
};


